import { keepPreviousData, queryOptions } from '@tanstack/react-query'
import type { QuotesFilterValues } from 'context/QuotesViewContext'
import { getRequest, postRequest } from 'global/helpers/requests'
import { type PaginatedResults } from 'global/types/pagination'
import { type QuoteAnswer, type QuoteRequest } from 'screens/QuoteDetails/QuoteDetails.types'
import type { QuoteRequestCounters } from './Quotes.types'

const PREFIX = 'carrier-quotes'

export const QUOTES_QUERY_KEY = 'quotes'
const QUOTES_COUNTERS_QUERY_KEY = 'counters'
const QUOTES_LIST_QUERY_KEY = 'list'
export const QUOTE_DETAILS_QUERY_KEY = 'quote-details'
const QUOTE_DETAILS_REQUEST_QUERY_KEY = 'request'
const QUOTE_DETAILS_ANSWER_QUERY_KEY = 'answer'

export const getQuoteRequestCounters = async (): Promise<QuoteRequestCounters> =>
  await getRequest<QuoteRequestCounters>('requests/counters', {}, PREFIX)

export const quoteRequestCountersQueryOptions = queryOptions({
  queryKey: [QUOTES_QUERY_KEY, QUOTES_COUNTERS_QUERY_KEY],
  queryFn: getQuoteRequestCounters,
})

export const getFilteredQuotes = async (
  page: number,
  filters: QuotesFilterValues,
): Promise<PaginatedResults<QuoteRequest>> => {
  const search = new URLSearchParams()
  filters.keywords.forEach((keyword) => search.append('keywords', keyword))
  filters.status.forEach((status) => search.append('status', status))
  if (filters.fromDate) {
    search.append('fromDate', filters.fromDate.toISOString())
  }
  if (filters.toDate) {
    search.append('toDate', filters.toDate.toISOString())
  }
  search.append('page', page.toString())
  return await getRequest<PaginatedResults<QuoteRequest>>(`requests?${search.toString()}`, {}, PREFIX)
}

export const filteredQuotesQueryOptions = (page: number, filters: QuotesFilterValues) => queryOptions({
  queryKey: [QUOTES_QUERY_KEY, QUOTES_LIST_QUERY_KEY, { page, filters }],
  queryFn: () => getFilteredQuotes(page, filters),
  placeholderData: keepPreviousData,
})

export const getQuote = async (token: string): Promise<QuoteRequest> =>
  await getRequest<QuoteRequest>(`requests/${token}`, {}, PREFIX)

export const quoteRequestQueryOptions = (token: string) => queryOptions({
  queryKey: [QUOTE_DETAILS_QUERY_KEY, token, QUOTE_DETAILS_REQUEST_QUERY_KEY],
  queryFn: () => getQuote(token),
})

export const getQuoteAnswer = async (token: string): Promise<QuoteAnswer> =>
  await getRequest<QuoteAnswer>(`requests/${token}/answer`, {}, PREFIX)

export const quoteAnswerQueryOptions = (token: string) => queryOptions({
  queryKey: [QUOTE_DETAILS_QUERY_KEY, token, QUOTE_DETAILS_ANSWER_QUERY_KEY],
  queryFn: () => getQuoteAnswer(token),
})

export const sendQuoteAnswer = async (token: string, data: QuoteAnswer): Promise<QuoteAnswer> =>
  await postRequest<QuoteAnswer>(`requests/${token}/answer`, { body: data }, PREFIX)
